/**
 * After an event's promoter / host links are saved (owner 2026-09-28, "Fully automatic").
 *
 * The DATABASE prepares the generic CTA at commit of the link save (migration 2026-09-28-001,
 * `_eib_auto_prepare_event_cta`): $20 + tracking codes from the promoter (gun show) or host venue
 * (range night), URL/QR cleared. The QR image and the "Scan to join" flyer are drawn in the browser, so
 * the saving screen finishes the job here. Both steps are no-ops when there is nothing to do: the QR
 * step skips a CTA that already has its URL and QR, and the flyer is made only for a QR generated now
 * on an eligible CTA. Never throws — a failure leaves "QR pending" / "Create flyer" on the CTA card.
 *
 * Callers AWAIT it (owner 2026-09-28: "everything should be done by the time they leave those
 * wizards"), so the QR and flyer exist before the save screen closes. Bounded by `timeoutMs`: past it
 * the save returns and the work carries on in the background; the Call to Action panel finishes
 * anything still missing the next time it opens.
 */
import type { Event } from "../types/event.types";
import {
  getEventCTABaseVariant,
  regenerateGenericCTAQRs,
} from "./eventCTA.service";
import {
  flyerEligibility,
  flyerLabel,
  saveCTAFlyerToEvent,
} from "./eventCTAFlyer.service";
import { supabase } from "./supabase";

export async function afterEventLinksSaved(
  eventId: string,
  userId: string,
  timeoutMs = 60000,
): Promise<{ qr: boolean; flyer: boolean }> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<{ qr: boolean; flyer: boolean }>((resolve) => {
    timer = setTimeout(() => resolve({ qr: false, flyer: false }), timeoutMs);
  });
  try {
    return await Promise.race([finishEventCTA(eventId, userId), timeout]);
  } finally {
    clearTimeout(timer);
  }
}

async function finishEventCTA(
  eventId: string,
  userId: string,
): Promise<{ qr: boolean; flyer: boolean }> {
  try {
    const result = await regenerateGenericCTAQRs([eventId], userId);
    const done = result.perEvent.find((r) => r.ctaId && !r.skipped && !r.error);
    if (!done?.ctaId) {
      return { qr: false, flyer: false };
    }
    const [{ data: cta }, { getEventById }] = await Promise.all([
      supabase
        .from("event_call_to_actions")
        .select("*")
        .eq("id", done.ctaId)
        .maybeSingle(),
      import("./events.service"),
    ]);
    const event = (await getEventById(eventId)) as Event | null;
    // Insurance-state event: a flyer with no amount is allowed (migration 410), only for an event type with insurance
    // artwork (range nights and gun shows; any other type is "artwork_pending" -> no automatic flyer, owner 2026-10-12).
    const variant = await getEventCTABaseVariant(eventId).catch(
      () => "standard" as const,
    );
    if (
      !cta ||
      !event ||
      !flyerEligibility(cta, event, { baseVariant: variant }).ok
    ) {
      return { qr: true, flyer: false };
    }
    try {
      await saveCTAFlyerToEvent({
        cta,
        event,
        label: flyerLabel(cta),
        userId,
      });
      return { qr: true, flyer: true };
    } catch (err) {
      // No Files access, or a transient failure: "Create flyer" on the CTA card is the retry.
      console.warn("Event CTA flyer was not saved:", err);
      return { qr: true, flyer: false };
    }
  } catch (err) {
    console.warn("Generic CTA QR generation failed:", err);
    return { qr: false, flyer: false };
  }
}
